import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AlertTriangle, ArrowRight, Database, Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/Badge';
import { CardSkeleton, EmptyState, ErrorState, Skeleton } from '@/components/ui/States';
import {
  formatBytes,
  formatCompact,
  formatDomain,
  formatNumber,
  formatRelative,
} from '@/lib/format';
import { useThemeStore } from '@/store/theme';
import { cn } from '@/lib/cn';

export const DashboardPage = () => {
  const navigate = useNavigate();

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['dashboard', 'overview'],
    queryFn: () => api.get('/dashboard/overview'),
  });

  if (isError) {
    return (
      <Card>
        <ErrorState message={error?.message} requestId={error?.requestId} onRetry={refetch} />
      </Card>
    );
  }

  const overview = data?.data;
  const stats = overview?.stats;

  return (
    <div className="space-y-4">
      {/* Baris teratas.

          Dulu isinya sapaan dan nama pengguna dalam huruf terbesar di halaman.
          Pengguna sudah tahu siapa dirinya, dan baris paling atas adalah ruang
          paling mahal yang ada. Sekarang ia menjawab pertanyaan yang memang
          dibawa orang saat membuka dashboard: apa yang terjadi sejak terakhir
          saya ke sini. */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        {isLoading ? (
          <Skeleton className="h-11 w-full max-w-lg" />
        ) : (
          <KalimatKeadaan stats={stats} usage={overview.usage} />
        )}

        <Button icon={Plus} onClick={() => navigate('/jobs/new')} className="shrink-0">
          Crawl Baru
        </Button>
      </div>

      {overview?.warnings?.length > 0 && <QuotaWarnings warnings={overview.warnings} />}

      {/* Perlu perhatian.

          Hanya dirender kalau memang ada yang gagal. Halaman yang selalu
          menampilkan blok peringatan, terisi atau tidak, akan berhenti dibaca
          justru pada hari peringatannya penting. */}
      {!isLoading && stats.failed > 0 && <PerluPerhatian jumlah={stats.failed} />}

      {isLoading ? (
        <CardSkeleton />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[1.55fr_1fr] lg:items-start">
          <div className="space-y-4">
            <RelAngka stats={stats} onPilih={(q) => navigate(q)} />

            <Card>
              <CardHeader
                title="Aktivitas crawl"
                description={`${formatNumber(stats.runsLast7Days)} run dalam 7 hari terakhir`}
              />
              <div className="p-5 pt-3">
                <ActivityChart data={overview.activity} />
              </div>
            </Card>
          </div>

          <PanelKuota usage={overview.usage} />
        </div>
      )}

      {/* Job terbaru */}
      <Card>
        <CardHeader
          title="Job terbaru"
          action={
            <Link
              to="/jobs"
              className="inline-flex items-center gap-1 text-sm font-semibold text-accent-600 hover:underline dark:text-accent-400"
            >
              Lihat semua
              <ArrowRight className="size-3.5" />
            </Link>
          }
        />

        {isLoading ? (
          <div className="divide-y divide-[var(--border-subtle)]">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4 px-5 py-4">
                <Skeleton className="h-4 flex-[2]" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-6 w-20 rounded-full" />
              </div>
            ))}
          </div>
        ) : overview.recentJobs.length === 0 ? (
          <EmptyState
            icon={Database}
            title="Belum ada job crawl"
            description="Tempel satu alamat halaman, biarkan Crawel menebak kolomnya, lalu jalankan. Sekali coba biasanya cukup untuk tahu ini cocok atau tidak."
            action={
              <Button icon={Plus} onClick={() => navigate('/jobs/new')}>
                Buat Job Pertama
              </Button>
            }
          />
        ) : (
          <ul className="divide-y divide-[var(--border-subtle)]">
            {overview.recentJobs.map((job) => (
              <BarisJob key={job.id} job={job} />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
};

/* ── Baris teratas ─────────────────────────────────────────────────────── */

/**
 * Kalimat keadaan.
 *
 * Seluruh angkanya sudah ada di balasan endpoint; tidak ada permintaan
 * tambahan. Yang berubah hanya bentuknya: empat angka yang berdiri sendiri
 * menuntut pembacanya menyusun sendiri artinya, satu kalimat tidak.
 *
 * Bagian yang tidak berlaku dibuang, bukan ditulis nol. "0 job gagal" tetap
 * memaksa mata membaca kata "gagal" pada hari ketika tidak ada yang gagal.
 */
const KalimatKeadaan = ({ stats, usage }) => {
  // Akun yang belum punya job apa pun tidak diberi kalimat sama sekali.
  // Halaman ini sudah memuat ajakan lengkap di bagian "Job terbaru", dan
  // menuliskannya dua kali dengan kata yang berbeda cuma membuat pengguna
  // baru bertanya apakah itu dua hal yang berbeda.
  if (stats.totalJobs === 0) return null;

  const bagian = [];

  const aktif = stats.running + stats.queued;
  if (aktif > 0) {
    bagian.push(
      <>
        <b className="font-extrabold">{formatNumber(aktif)} job</b> sedang berjalan
      </>,
    );
  }

  if (stats.failed > 0) {
    bagian.push(
      <>
        <b className="font-extrabold text-danger-600 dark:text-danger-500">
          {formatNumber(stats.failed)} gagal
        </b>{' '}
        belum ditangani
      </>,
    );
  }

  // Kuota hanya disebut setelah angkanya berarti. "Kuota terpakai 0%" memakai
  // baris paling mahal di halaman untuk mengabarkan bahwa tidak ada kabar.
  if (usage?.records?.limit > 0 && usage.records.percent >= 1) {
    bagian.push(
      <>
        kuota baris terpakai <b className="font-extrabold">{usage.records.percent}%</b>
      </>,
    );
  }

  // Tidak ada yang berjalan dan tidak ada yang gagal: keadaan yang tenang
  // berhak atas kalimatnya sendiri, bukan dibiarkan kosong.
  if (bagian.length === 0) {
    return (
      <div className="min-w-0">
        <p className="text-[17px] leading-snug font-medium text-[var(--text-primary)] sm:text-lg">
          Semua job dalam keadaan baik.{' '}
          <b className="font-extrabold">{formatNumber(stats.totalJobs)}</b> job tersimpan.
        </p>
        <Periode usage={usage} />
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <p className="text-[17px] leading-snug font-medium text-[var(--text-primary)] sm:text-lg">
        {bagian.map((b, i) => (
          <span key={i}>
            {i > 0 && (i === bagian.length - 1 ? ', dan ' : ', ')}
            {b}
          </span>
        ))}
        .
      </p>
      <Periode usage={usage} />
    </div>
  );
};

/** Baris kecil di bawah kalimat keadaan: paket apa, dan sampai kapan. */
const Periode = ({ usage }) =>
  usage?.periodEnd ? (
    <p className="mt-1 text-xs text-[var(--text-muted)]">
      Paket {usage.plan?.name} &middot; periode berakhir {formatRelative(usage.periodEnd)}
    </p>
  ) : null;

/** Blok yang hanya muncul kalau memang ada yang perlu ditangani. */
const PerluPerhatian = ({ jumlah }) => (
  <Link
    to="/jobs?status=failed"
    className={cn(
      'flex items-center gap-3 rounded-r-xl border-l-[3px] border-danger-500 px-4 py-3',
      'bg-danger-50 transition-colors hover:bg-danger-100',
      'dark:bg-danger-500/10 dark:hover:bg-danger-500/15',
    )}
  >
    <AlertTriangle
      className="size-[18px] shrink-0 text-danger-600 dark:text-danger-500"
      aria-hidden="true"
    />
    <p className="flex-1 text-sm font-semibold text-danger-700 dark:text-danger-500">
      {formatNumber(jumlah)} job berhenti dengan kegagalan.
    </p>
    <span className="shrink-0 text-sm font-bold text-danger-700 underline underline-offset-2 dark:text-danger-500">
      Lihat penyebabnya
    </span>
  </Link>
);

/* ── Rel angka ─────────────────────────────────────────────────────────── */

/**
 * Empat angka dalam satu kotak bersekat, bukan empat kotak melayang.
 *
 * Sel "Gagal" berlatar merah muda HANYA saat angkanya bukan nol. Menandai
 * sesuatu yang tidak perlu ditindaklanjuti mengajari pengguna mengabaikan
 * warnanya, dan sesudah itu warna yang sama tidak lagi berguna saat benar-
 * benar dibutuhkan.
 */
const RelAngka = ({ stats, onPilih }) => {
  const sel = [
    { label: 'Total job', nilai: stats.totalJobs, ke: '/jobs', tren: stats.trendPercent },
    {
      label: 'Berjalan',
      nilai: stats.running + stats.queued,
      ke: '/jobs?status=running',
      hidup: stats.running > 0,
    },
    { label: 'Berhasil', nilai: stats.completed, ke: '/jobs?status=completed' },
    { label: 'Gagal', nilai: stats.failed, ke: '/jobs?status=failed', sorot: stats.failed > 0 },
  ];

  return (
    <Card className="flex overflow-hidden">
      {sel.map((s) => (
        <button
          key={s.label}
          type="button"
          onClick={() => onPilih(s.ke)}
          className={cn(
            'flex-1 border-r border-[var(--border-subtle)] px-4 py-3.5 text-left last:border-r-0',
            'transition-colors',
            s.sorot
              ? 'bg-danger-50 hover:bg-danger-100 dark:bg-danger-500/10 dark:hover:bg-danger-500/15'
              : 'hover:bg-[var(--surface-sunken)]',
          )}
        >
          <span
            className={cn(
              'text-[11px] font-bold tracking-wide uppercase',
              s.sorot ? 'text-danger-600 dark:text-danger-500' : 'text-[var(--text-muted)]',
            )}
          >
            {s.label}
          </span>

          <span className="mt-1 flex items-baseline gap-1.5">
            <span
              className={cn(
                'text-xl font-extrabold tracking-tight tabular sm:text-2xl',
                s.sorot ? 'text-danger-600 dark:text-danger-500' : 'text-[var(--text-primary)]',
              )}
            >
              {formatNumber(s.nilai)}
            </span>

            {s.hidup && (
              <span
                className="size-1.5 animate-pulse rounded-full bg-success-500"
                aria-label="sedang berjalan"
              />
            )}

            {/* Tanda +/- ikut ditulis: warna saja tidak pernah cukup untuk
                menyampaikan arti kepada pengguna yang buta warna. */}
            {typeof s.tren === 'number' && s.tren !== 0 && (
              <span
                className={cn(
                  'text-[11px] font-bold',
                  s.tren > 0
                    ? 'text-success-600 dark:text-success-500'
                    : 'text-[var(--text-muted)]',
                )}
              >
                {s.tren > 0 ? '+' : ''}
                {s.tren}%
              </span>
            )}
          </span>
        </button>
      ))}
    </Card>
  );
};

/* ── Panel kuota ───────────────────────────────────────────────────────── */

/**
 * Pemakaian paket.
 *
 * Datanya sudah ikut terkirim di `overview.usage` sejak lama, tetapi halaman
 * ini membuangnya dan hanya memakainya untuk memunculkan peringatan. Akibatnya
 * pengguna baru tahu kuotanya menipis saat sudah hampir habis. Menampilkannya
 * tidak menambah satu pun permintaan ke server.
 */
const PanelKuota = ({ usage }) => {
  if (!usage) return null;

  const baris = [
    {
      nama: 'Baris terkumpul',
      nilai: `${formatCompact(usage.records.used)} / ${formatCompact(usage.records.limit)}`,
      persen: usage.records.percent,
    },
    {
      nama: 'Penyimpanan',
      nilai: `${formatBytes(usage.storage.used)} / ${formatBytes(usage.storage.limit)}`,
      persen: usage.storage.percent,
    },
    {
      nama: 'Job aktif',
      nilai: `${formatNumber(usage.jobs.used)} / ${formatNumber(usage.jobs.limit)}`,
      persen: usage.jobs.percent,
    },
  ];

  return (
    <Card>
      <CardHeader
        title="Pemakaian paket"
        description={usage.plan?.name}
        action={
          <Link
            to="/billing"
            className="text-sm font-semibold text-accent-600 hover:underline dark:text-accent-400"
          >
            Kelola
          </Link>
        }
      />

      <div className="divide-y divide-[var(--border-subtle)]">
        {baris.map((b) => (
          <div key={b.nama} className="px-5 py-3.5">
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="font-semibold text-[var(--text-secondary)]">{b.nama}</span>
              <span className="tabular text-[var(--text-muted)]">{b.nilai}</span>
            </div>

            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[var(--surface-sunken)]">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-500',
                  b.persen >= 90
                    ? 'bg-danger-500'
                    : b.persen >= 75
                      ? 'bg-warning-500'
                      : 'bg-accent-500',
                )}
                style={{ width: `${Math.min(100, b.persen)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

/* ── Baris job ─────────────────────────────────────────────────────────── */

/**
 * Satu baris pada daftar job terbaru.
 *
 * Job yang gagal menampilkan sebabnya di tempat domain. Saat sebuah job gagal,
 * itulah satu-satunya hal yang ingin diketahui, dan menampilkan nama domain
 * yang sudah kelihatan dari nama jobnya tidak menolong siapa pun.
 */
const BarisJob = ({ job }) => {
  const gagal = job.status === 'failed';
  const alasan = job.lastError?.message ?? null;

const PITA = {
  completed: 'bg-success-500',
  running: 'bg-accent-500',
  queued: 'bg-accent-500',
  scheduled: 'bg-warning-500',
  failed: 'bg-danger-500',
  draft: 'bg-ink-300',
};

  return (
    <li>
      <Link
        to={`/jobs/${job.id}`}
        className="flex items-center gap-3 px-5 py-3 transition-colors hover:bg-[var(--surface-sunken)]"
      >
        {/* Pita status: bentuk, bukan cuma warna. Terbaca lebih dulu daripada
            pil di ujung kanan, dan tetap terbaca saat baris dipindai cepat. */}
        <span
          className={cn('h-8 w-[3px] shrink-0 rounded-full', PITA[job.status] ?? 'bg-ink-300')}
          aria-hidden="true"
        />

        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-[var(--text-primary)]">{job.name}</p>
          <p
            className={cn(
              'truncate text-xs',
              gagal && alasan ? 'text-danger-600 dark:text-danger-500' : 'text-[var(--text-muted)]',
            )}
          >
            {gagal && alasan
              ? alasan
              : `${formatDomain(job.targetUrl)} · ${formatRelative(job.stats?.lastRunAt)}`}
          </p>
        </div>

        <div className="hidden shrink-0 text-right sm:block">
          <p className="text-sm font-bold tabular text-[var(--text-primary)]">
            {formatCompact(job.stats?.totalRecords ?? 0)}
          </p>
          <p className="text-[10px] font-bold tracking-wide text-[var(--text-muted)] uppercase">
            baris
          </p>
        </div>

        <div className="shrink-0">
          <StatusBadge status={job.status} size="sm" />
        </div>
      </Link>
    </li>
  );
};


/** Banner peringatan kuota - muncul sebelum pengguna kehabisan, bukan sesudah. */
const QuotaWarnings = ({ warnings }) => (
  <div className="space-y-2">
    {warnings.map((warning) => (
      <div
        key={warning.resource}
        role="alert"
        className={cn(
          'flex items-start gap-3 rounded-r-xl border-l-[3px] px-4 py-3',
          warning.severity === 'error'
            ? 'border-danger-500 bg-danger-50 dark:bg-danger-500/10'
            : 'border-warning-500 bg-warning-50 dark:bg-warning-500/10',
        )}
      >
        <AlertTriangle
          className={cn(
            'mt-0.5 size-[18px] shrink-0',
            warning.severity === 'error'
              ? 'text-danger-600 dark:text-danger-500'
              : 'text-warning-600 dark:text-warning-500',
          )}
          aria-hidden="true"
        />
        <p
          className={cn(
            'flex-1 text-sm font-medium',
            warning.severity === 'error'
              ? 'text-danger-700 dark:text-danger-500'
              : 'text-warning-700 dark:text-warning-500',
          )}
        >
          {warning.message}
        </p>
        <Link
          to="/billing"
          className="shrink-0 text-sm font-bold underline underline-offset-2 hover:no-underline"
        >
          Naikkan paket
        </Link>
      </div>
    ))}
  </div>
);

/* ── Grafik ────────────────────────────────────────────────────────────── */

/**
 * Grafik aktivitas.
 *
 * Batang bertumpuk berubah jadi area bertumpuk. Tujuh batang terpisah menuntut
 * mata membandingkan satu per satu; garis menunjukkan arahnya seketika. Yang
 * ingin dijawab dari bagian ini adalah "naik atau turun", bukan "hari Rabu
 * tepatnya berapa" - dan pertanyaan kedua tetap terjawab oleh tooltip.
 */
const ActivityChart = ({ data }) => {
  const isDark = useThemeStore((s) => s.isDark);
  const hasData = data.some((d) => d.total > 0);

  if (!hasData) {
    return (
      <EmptyState
        compact
        icon={Database}
        title="Belum ada aktivitas"
        description="Grafik akan terisi setelah job pertama Anda dijalankan."
      />
    );
  }

  const grid = isDark ? '#262d38' : '#e3e6eb';
  const axis = isDark ? '#7c8697' : '#6b7683';

  return (
    <ResponsiveContainer width="100%" height={220}>
      <AreaChart data={data} margin={{ top: 8, right: 4, bottom: 0, left: -18 }}>
        <defs>
          <linearGradient id="isianBerhasil" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3252ad" stopOpacity={0.28} />
            <stop offset="100%" stopColor="#3252ad" stopOpacity={0.02} />
          </linearGradient>
          <linearGradient id="isianGagal" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f04438" stopOpacity={0.3} />
            <stop offset="100%" stopColor="#f04438" stopOpacity={0.03} />
          </linearGradient>
        </defs>

        <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
        <XAxis
          dataKey="label"
          tickLine={false}
          axisLine={false}
          tick={{ fill: axis, fontSize: 12 }}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tick={{ fill: axis, fontSize: 12 }}
          allowDecimals={false}
          width={40}
        />
        <Tooltip
          cursor={{ stroke: axis, strokeWidth: 1, strokeDasharray: '3 3' }}
          content={<ChartTooltip />}
        />

        {/* Tetap ditumpuk: hari yang "sibuk tapi banyak gagal" harus tetap
            terbaca sebagai satu tinggi total, sama seperti sebelumnya. */}
        <Area
          type="monotone"
          dataKey="successful"
          name="Berhasil"
          stackId="a"
          stroke="#3252ad"
          strokeWidth={2}
          fill="url(#isianBerhasil)"
        />
        <Area
          type="monotone"
          dataKey="failed"
          name="Gagal"
          stackId="a"
          stroke="#f04438"
          strokeWidth={2}
          fill="url(#isianGagal)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const point = payload[0].payload;

  return (
    <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-overlay)] px-3 py-2 shadow-lg">
      <p className="text-xs font-bold text-[var(--text-primary)]">{label}</p>
      <dl className="mt-1.5 space-y-0.5 text-xs">
        <div className="flex items-center justify-between gap-4">
          <dt className="text-[var(--text-muted)]">Berhasil</dt>
          <dd className="font-semibold tabular text-success-600">{point.successful}</dd>
        </div>
        {point.failed > 0 && (
          <div className="flex items-center justify-between gap-4">
            <dt className="text-[var(--text-muted)]">Gagal</dt>
            <dd className="font-semibold tabular text-danger-600">{point.failed}</dd>
          </div>
        )}
        <div className="flex items-center justify-between gap-4 border-t border-[var(--border-subtle)] pt-0.5">
          <dt className="text-[var(--text-muted)]">Baris data</dt>
          <dd className="font-semibold tabular text-[var(--text-primary)]">
            {formatCompact(point.records)}
          </dd>
        </div>
      </dl>
    </div>
  );
};
